interface ActivityInterface {
  getText(): string
  getTextParams(): Record<string, unknown>
  getIconColor(): string
  getType(): string
}
interface Detaillable {
  getDetailTitle(): string
  getDetailText(): string
}

import useDate from '@/composables/date'

import type { Icons } from '@/components/base/BaseIcon.vue'

const { formatTime, localDate } = useDate()

class Activity {
  date: Date
  getType():
    | 'answerCrm'
    | 'message'
    | 'manualPoint'
    | 'importedPoint'
    | 'checkin'
    | 'contestGameParticipation'
    | 'reward'
    | 'missedCalls'
    | 'survey'
    | 'autopilot'
    | 'marketingFlow'
    | 'webapp'
    | '' {
    return ''
  }
  getDate() {
    return localDate(this.date)
  }
  getTime() {
    return formatTime(this.date)
  }
  getId(): string {
    return `${this.getType()}${Math.random() * 1000}`
  }
  isDetaillable(): boolean {
    return false
  }
  getIconColor(): string {
    return ''
  }
  getText(): string {
    return ''
  }
  getDetailTitle(): string {
    return ''
  }
  getDetailText(): string {
    return ''
  }
  getConversionDateText(): string {
    return ''
  }
  getConversionDateTextParams(): Record<string, unknown> {
    return {}
  }
  getTextParams(): Record<string, unknown> {
    return {}
  }
  isPending(): boolean {
    return false
  }
  getIcon(): Icons | null {
    return null
  }
  isConverted(): boolean {
    return false
  }
  isFeedActivity(): boolean {
    return false
  }
}
class ActivityDetaillable extends Activity {
  isDetaillable(): boolean {
    return true
  }
}

class AnswerCrm extends Activity implements ActivityInterface {
  answer: string
  question: string

  getText(): string {
    if (!this.answer) {
      return 'pages.customer.customerDetailPage.blocks.activity.answerCrm.noAnswer'
    }
    return 'pages.customer.customerDetailPage.blocks.activity.answerCrm.text'
  }
  getTextParams(): Record<string, unknown> {
    return {
      question: this.question,
      answer: this.answer
    }
  }
  getIconColor(): string {
    return 'bg-foreground-disabled'
  }
  getIcon(): Icons {
    return 'HeroIdentificationIconSolid'
  }
  getType(): 'answerCrm' {
    return 'answerCrm'
  }
}

class Message extends ActivityDetaillable implements ActivityInterface, Detaillable {
  convertedAt: Date | null
  content: string
  messageChannel: string
  sentAt: Date | null

  getText(): string {
    const channel = this.messageChannel || 'sms'
    const text = this.isPending()
      ? `pages.customer.customerDetailPage.blocks.activity.common.text.pending.${channel}`
      : `pages.customer.customerDetailPage.blocks.activity.common.text.${channel}`

    return text
  }
  getTextParams(): Record<string, unknown> {
    return {}
  }
  getDetailTitle(): string {
    return 'pages.customer.customerDetailPage.blocks.activity.common.detail.title'
  }
  getDetailText(): string {
    return this.content
  }
  isConverted(): boolean {
    return this.convertedAt !== null
  }
  isPending(): boolean {
    return Boolean(!this.sentAt)
  }
  getConversionDateText(): string {
    return 'pages.customer.customerDetailPage.blocks.activity.common.detail.conversionDate'
  }
  getConversionDateTextParams(): Record<string, unknown> {
    return {
      date: localDate(this.convertedAt),
      time: formatTime(this.convertedAt)
    }
  }
  getIconColor(): string {
    return 'bg-green-400'
  }
  getIcon(): Icons {
    switch (this.messageChannel) {
      case 'sms':
        return 'HeroChatBubbleOvalLeftEllipsisIconSolid'
      case 'email':
        return 'HeroInboxIconSolid'
      case 'push':
        return 'HeroBellIconSolid'
      default:
        return 'HeroChatBubbleOvalLeftEllipsisIconSolid'
    }
  }
  getType(): 'message' {
    return 'message'
  }
}

class ManualPoint extends Activity implements ActivityInterface {
  points: number

  getText(): string {
    return 'pages.customer.customerDetailPage.blocks.activity.manualPoints.text'
  }
  getTextParams(): Record<string, unknown> {
    return {
      points: this.points
    }
  }
  getIconColor(): string {
    return 'bg-pink-600'
  }
  getIcon(): Icons {
    return 'HeroCursorArrowRaysIconSolid'
  }
  getType(): 'manualPoint' {
    return 'manualPoint'
  }
}

class ImportedPoint extends Activity implements ActivityInterface {
  points: number

  getText(): string {
    return 'pages.customer.customerDetailPage.blocks.activity.importedPoints.text'
  }
  getTextParams(): Record<string, unknown> {
    return {
      points: this.points
    }
  }
  getIconColor(): string {
    return 'bg-emerald-600'
  }
  getIcon(): Icons {
    return 'HeroDocumentArrowDownIconSolid'
  }
  getType(): 'importedPoint' {
    return 'importedPoint'
  }
}

class Checkin extends Activity implements ActivityInterface {
  points: number
  store: string

  getText(): string {
    if (this.points === 0) {
      return 'pages.customer.customerDetailPage.blocks.activity.checkinNoPoint.text'
    }
    return 'pages.customer.customerDetailPage.blocks.activity.checkin.text'
  }
  getTextParams(): Record<string, unknown> {
    return {
      points: this.points,
      store: this.store
    }
  }
  getIconColor(): string {
    return 'bg-yellow-400'
  }
  getIcon(): Icons {
    return 'HeroMapPinIconSolid'
  }
  getType(): 'checkin' {
    return 'checkin'
  }
}

class ContestGameParticipation extends Activity implements ActivityInterface {
  reward: string
  store: string
  win: boolean

  getText(): string {
    if (this.win) {
      return 'pages.customer.customerDetailPage.blocks.activity.contestGameParticipation.text.win'
    } else {
      return 'pages.customer.customerDetailPage.blocks.activity.contestGameParticipation.text.lose'
    }
  }
  getTextParams(): Record<string, unknown> {
    return {
      reward: this.reward,
      store: this.store
    }
  }
  getIconColor(): string {
    return 'bg-red-400'
  }
  getIcon(): Icons {
    return 'HeroPlayIconSolid'
  }
  getType(): 'contestGameParticipation' {
    return 'contestGameParticipation'
  }
}

class Reward extends Activity implements ActivityInterface {
  text: string

  getText(): string {
    return 'pages.customer.customerDetailPage.blocks.activity.reward.text'
  }
  getTextParams(): Record<string, unknown> {
    return {
      reward: this.text
    }
  }
  getIconColor(): string {
    return 'bg-primary'
  }
  getIcon(): Icons {
    return 'HeroGiftIconSolid'
  }
  getType(): 'reward' {
    return 'reward'
  }
}

class MissedCalls extends Activity implements ActivityInterface {
  store: string
  isClicked: boolean

  getText(): string {
    if (this.isClicked) {
      return 'pages.customer.customerDetailPage.blocks.activity.missedCall.text.isClicked'
    } else {
      return 'pages.customer.customerDetailPage.blocks.activity.missedCall.text.isNotClicked'
    }
  }
  getTextParams(): Record<string, unknown> {
    return {
      store: this.store
    }
  }
  getIconColor(): string {
    return 'bg-indigo-400'
  }
  getIcon(): Icons {
    return 'HeroPhoneXMarkIconSolid'
  }
  getType(): 'missedCalls' {
    return 'missedCalls'
  }
}

class Survey extends ActivityDetaillable implements ActivityInterface, Detaillable {
  choice: string
  question: string

  getText(): string {
    if (this.choice === null) {
      return 'pages.customer.customerDetailPage.blocks.activity.survey.text.noAnswer'
    } else {
      return 'pages.customer.customerDetailPage.blocks.activity.survey.text.answer'
    }
  }
  getTextParams(): Record<string, unknown> {
    return {
      choice: this.choice
    }
  }
  getDetailTitle(): string {
    return 'pages.customer.customerDetailPage.blocks.activity.survey.detail.title'
  }
  getDetailText(): string {
    return this.question
  }
  getIconColor(): string {
    return 'bg-indigo-400'
  }
  getIcon(): Icons {
    return 'HeroChartPieIconSolid'
  }
  getType(): 'survey' {
    return 'survey'
  }
}

class Autopilot extends ActivityDetaillable implements ActivityInterface, Detaillable {
  convertedAt: Date
  points: number
  message: string

  getText(): string {
    return this.points === 0
      ? 'pages.customer.customerDetailPage.blocks.activity.autopilot.withoutPointText'
      : 'pages.customer.customerDetailPage.blocks.activity.autopilot.withPointText'
  }

  getTextParams(): Record<string, unknown> {
    return {
      points: this.points,
      message: this.message
    }
  }
  getDetailTitle(): string {
    return 'pages.customer.customerDetailPage.blocks.activity.autopilot.detail.title'
  }
  getDetailText(): string {
    return this.message
  }
  isConverted(): boolean {
    return this.convertedAt !== null
  }
  getConversionDateText(): string {
    return 'pages.customer.customerDetailPage.blocks.activity.autopilot.detail.conversionDate'
  }
  getConversionDateTextParams(): Record<string, unknown> {
    return {
      convertedAt: this.convertedAt
    }
  }
  getIconColor(): string {
    return 'bg-yellow-400'
  }
  getIcon(): Icons {
    return 'HeroBoltIconSolid'
  }
  getType(): 'autopilot' {
    return 'autopilot'
  }
}

class MarketingFlow extends Activity implements ActivityInterface {
  marketingFlow: {
    name: string
  }
  points: number

  getText(): string {
    return 'pages.customer.customerDetailPage.blocks.activity.marketingFlow.text'
  }
  getTextParams(): Record<string, unknown> {
    return {
      points: this.points,
      name: this.marketingFlow.name
    }
  }
  getIconColor(): string {
    return 'bg-yellow-400'
  }
  getIcon(): Icons {
    return 'HeroBoltIconSolid'
  }
  getType(): 'marketingFlow' {
    return 'marketingFlow'
  }
}

class WebApp extends Activity implements ActivityInterface {
  points: number
  store: string
  type: string

  getText(): string {
    return `pages.customer.customerDetailPage.blocks.activity.${this.type}.text`
  }
  getTextParams(): Record<string, unknown> {
    return {
      points: this.points,
      store: this.store
    }
  }
  getIconColor(): string {
    return 'bg-amber-500'
  }
  getIcon(): Icons {
    return 'HeroCircleStackIconSolid'
  }
  getType(): 'webapp' {
    return 'webapp'
  }
}

export {
  Activity,
  AnswerCrm,
  Autopilot,
  Checkin,
  ContestGameParticipation,
  ImportedPoint,
  ManualPoint,
  MarketingFlow,
  Message,
  MissedCalls,
  Reward,
  Survey,
  WebApp
}
